import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { FiEdit2, FiKey, FiSearch } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const toBoolString = (value) => {
  if (value === true || value === 1 || value === "1") {
    return "1";
  }
  if (value === false || value === 0 || value === "0") {
    return "0";
  }
  return "";
};

const SystemOauthUsers = () => {
  const scopeValue = localStorage.getItem("rmc2.scope") || "";
  const scopes = scopeValue
    .split(" ")
    .map((scope) => scope.trim())
    .filter(Boolean);
  const isSystemAdmin = scopes.includes("system_admin");

  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("");
  const [accountStatusOptions, setAccountStatusOptions] = useState([]);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editError, setEditError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [activeUserId, setActiveUserId] = useState(null);
  const [banks, setBanks] = useState([]);
  const [formData, setFormData] = useState({
    oauth_username: "",
    oauth_email: "",
    oauth_first_name: "",
    oauth_last_name: "",
    oauth_scope: "",
    oauth_active: "1",
    oauth_email_verified: "1",

    profile_first_name: "",
    profile_middle_name: "",
    profile_last_name: "",
    profile_suffix: "",
    profile_date_of_birth: "",
    profile_gender: "",
    profile_civil_status_id: "",
    profile_nationality_id: "",
    profile_home_address: "",
    profile_id_type_id: "",
    profile_mobile_number: "",
    profile_email: "",
    profile_guardian_name: "",
    profile_relationship_to_principal: "",
    profile_account_status_id: "",

    bank_id: "",
    bank_account_name: "",
    bank_account_number: "",
    bank_is_primary: "1",
  });

  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [isPasswordSaving, setIsPasswordSaving] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    new_password: "",
    confirm_new_password: "",
  });

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  useEffect(() => {
    const fetchRows = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/oauth-users", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query || undefined,
            scope: scopeFilter || undefined,
            active: activeFilter || undefined,
            email_verified: emailVerifiedFilter || undefined,
            account_status_id: accountStatusFilter || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load OAuth users.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
        setAccountStatusOptions(data.account_status_options || []);
      } catch (error) {
        setLoadError("Failed to load OAuth users.");
      } finally {
        setIsLoading(false);
      }
    };

    if (isSystemAdmin) {
      fetchRows();
    }
  }, [
    query,
    scopeFilter,
    activeFilter,
    emailVerifiedFilter,
    accountStatusFilter,
    sortBy,
    sortDir,
    page,
    perPage,
    refreshKey,
    isSystemAdmin,
  ]);

  if (!isSystemAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const handleOpenEdit = async (id) => {
    setIsEditOpen(true);
    setEditError("");
    setActiveUserId(id);

    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setEditError("Missing access token.");
        return;
      }

      const response = await api.get(`/oauth-users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        setEditError("Failed to load user details.");
        return;
      }

      const data = response.data?.data || {};
      const oauthUser = data.oauth_user || {};
      const profile = data.profile || {};
      const bank = data.bank_account || {};

      setBanks(data.banks || []);
      setAccountStatusOptions(data.account_status_options || []);
      setFormData({
        oauth_username: oauthUser.username || "",
        oauth_email: oauthUser.email || "",
        oauth_first_name: oauthUser.first_name || "",
        oauth_last_name: oauthUser.last_name || "",
        oauth_scope: oauthUser.scope || "",
        oauth_active: toBoolString(oauthUser.active),
        oauth_email_verified: toBoolString(oauthUser.email_verified),

        profile_first_name: profile.first_name || "",
        profile_middle_name: profile.middle_name || "",
        profile_last_name: profile.last_name || "",
        profile_suffix: profile.suffix || "",
        profile_date_of_birth: profile.date_of_birth || "",
        profile_gender: profile.gender || "",
        profile_civil_status_id: profile.civil_status_id || "",
        profile_nationality_id: profile.nationality_id || "",
        profile_home_address: profile.home_address || "",
        profile_id_type_id: profile.id_type_id || "",
        profile_mobile_number: profile.mobile_number || "",
        profile_email: profile.email || "",
        profile_guardian_name: profile.guardian_name || "",
        profile_relationship_to_principal: profile.relationship_to_principal || "",
        profile_account_status_id: profile.account_status_id || "",

        bank_id: bank.bank_id || "",
        bank_account_name: bank.account_name || "",
        bank_account_number: bank.account_number || "",
        bank_is_primary: toBoolString(bank.is_primary),
      });
    } catch (error) {
      setEditError("Failed to load user details.");
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (!activeUserId) {
      return;
    }

    setIsSaving(true);
    setEditError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setEditError("Missing access token.");
        return;
      }

      const payload = {
        oauth_username: formData.oauth_username,
        oauth_email: formData.oauth_email || null,
        oauth_first_name: formData.oauth_first_name || null,
        oauth_last_name: formData.oauth_last_name || null,
        oauth_scope: formData.oauth_scope || null,
        oauth_active: formData.oauth_active === "1",
        oauth_email_verified: formData.oauth_email_verified === "1",

        profile_first_name: formData.profile_first_name || null,
        profile_middle_name: formData.profile_middle_name || null,
        profile_last_name: formData.profile_last_name || null,
        profile_suffix: formData.profile_suffix || null,
        profile_date_of_birth: formData.profile_date_of_birth || null,
        profile_gender: formData.profile_gender || null,
        profile_civil_status_id: formData.profile_civil_status_id || null,
        profile_nationality_id: formData.profile_nationality_id || null,
        profile_home_address: formData.profile_home_address || null,
        profile_id_type_id: formData.profile_id_type_id || null,
        profile_mobile_number: formData.profile_mobile_number || null,
        profile_email: formData.profile_email || null,
        profile_guardian_name: formData.profile_guardian_name || null,
        profile_relationship_to_principal: formData.profile_relationship_to_principal || null,
        profile_account_status_id: formData.profile_account_status_id || null,

        bank_id: formData.bank_id || null,
        bank_account_name: formData.bank_account_name || null,
        bank_account_number: formData.bank_account_number || null,
        bank_is_primary: formData.bank_is_primary === "1",
      };

      const response = await api.put(`/oauth-users/${activeUserId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to save OAuth user.";
        if (message && typeof message === "object") {
          setEditError(Object.values(message).flat().join(" "));
        } else {
          setEditError(message);
        }
        return;
      }

      setIsEditOpen(false);
      setRefreshKey((prev) => prev + 1);
    } catch (error) {
      setEditError("Failed to save OAuth user.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenPassword = (id) => {
    setActiveUserId(id);
    setPasswordError("");
    setPasswordForm({ new_password: "", confirm_new_password: "" });
    setIsPasswordOpen(true);
  };

  const handleSavePassword = async (event) => {
    event.preventDefault();
    if (!activeUserId) {
      return;
    }

    setIsPasswordSaving(true);
    setPasswordError("");
    try {
      const token = localStorage.getItem("rmc2.access_token");
      if (!token) {
        setPasswordError("Missing access token.");
        return;
      }

      const response = await api.put(
        `/oauth-users/${activeUserId}/password`,
        {
          new_password: passwordForm.new_password,
          confirm_new_password: passwordForm.confirm_new_password,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data?.status !== "success") {
        const message = response.data?.message || "Failed to update password.";
        if (message && typeof message === "object") {
          setPasswordError(Object.values(message).flat().join(" "));
        } else {
          setPasswordError(message);
        }
        return;
      }

      setIsPasswordOpen(false);
    } catch (error) {
      setPasswordError("Failed to update password.");
    } finally {
      setIsPasswordSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
            System Admin
          </p>
          <h1 className="mt-2 text-2xl font-semibold text-slate-900">
            OAuth Users Management
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            Search, filter, and maintain OAuth accounts with connected profile and bank details.
          </p>
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <form onSubmit={handleSearchSubmit} className="relative xl:col-span-2">
            <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              type="text"
              placeholder="Search username, email, name, referral, mobile"
              className="w-full rounded-2xl border border-slate-200 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
            />
          </form>

          <input
            value={scopeFilter}
            onChange={(event) => {
              setScopeFilter(event.target.value);
              setPage(1);
            }}
            type="text"
            placeholder="Scope filter"
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
          />

          <select
            value={activeFilter}
            onChange={(event) => {
              setActiveFilter(event.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
          >
            <option value="">All Active</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>

          <select
            value={emailVerifiedFilter}
            onChange={(event) => {
              setEmailVerifiedFilter(event.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
          >
            <option value="">All Email Verified</option>
            <option value="1">Verified</option>
            <option value="0">Not Verified</option>
          </select>

          <select
            value={accountStatusFilter}
            onChange={(event) => {
              setAccountStatusFilter(event.target.value);
              setPage(1);
            }}
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
          >
            <option value="">All Account Status</option>
            {accountStatusOptions.map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadError && (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
          {loadError}
        </div>
      )}

      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-6 py-4 text-sm text-slate-500">
          <span>{isLoading ? "Loading users..." : `${total} user(s)`}</span>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
              Rows
            </span>
            <select
              value={perPage}
              onChange={(event) => {
                setPerPage(Number(event.target.value));
                setPage(1);
              }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-1 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-[1400px] w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-400">
              <tr>
                {[
                  { key: "username", label: "Username" },
                  { key: "email", label: "Email" },
                  { key: "first_name", label: "Name" },
                  { key: "scope", label: "Scope" },
                  { key: "active", label: "Active" },
                  { key: "email_verified", label: "Email Verified" },
                  { key: "mobile_number", label: "Mobile" },
                  { key: "account_status", label: "Account Status" },
                  { key: "bank_name", label: "Bank" },
                  { key: "created_at", label: "Created" },
                ].map((column) => (
                  <th
                    key={column.key}
                    onClick={() => handleSort(column.key)}
                    className="cursor-pointer px-4 py-4 text-left"
                  >
                    <div className="flex items-center gap-2">
                      {column.label}
                      {sortBy === column.key && <span>{sortDir === "asc" ? "▲" : "▼"}</span>}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {rows.map((row) => (
                <tr key={row.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 font-semibold text-slate-900">{row.username || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.email || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.last_name || "-"}, {row.first_name || "-"}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.scope || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{Number(row.active) === 1 ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-slate-600">{Number(row.email_verified) === 1 ? "Yes" : "No"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.mobile_number || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.account_status_name || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">{row.bank_name || "-"}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleOpenEdit(row.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                      >
                        <FiEdit2 />
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleOpenPassword(row.id)}
                        className="inline-flex items-center gap-1 rounded-full border border-amber-200 px-3 py-1 text-xs font-semibold text-amber-700 transition hover:border-amber-300"
                      >
                        <FiKey />
                        Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {!isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-slate-500">
                    No OAuth users found.
                  </td>
                </tr>
              )}
              {isLoading && (
                <tr>
                  <td colSpan={11} className="px-6 py-12 text-center text-slate-500">
                    Loading OAuth users...
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 text-sm text-slate-500">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {isEditOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  OAuth Users
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">Edit User</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsEditOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {editError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {editError}
              </div>
            )}

            <form onSubmit={handleSave} className="mt-6 space-y-6">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">OAuth Account</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {[
                    ["Username", "oauth_username"],
                    ["Email", "oauth_email"],
                    ["First Name", "oauth_first_name"],
                    ["Last Name", "oauth_last_name"],
                    ["Scope", "oauth_scope"],
                  ].map(([label, key]) => (
                    <div key={key}>
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</label>
                      <input
                        value={formData[key]}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, [key]: event.target.value }))
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                      />
                    </div>
                  ))}

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Active</label>
                    <select
                      value={formData.oauth_active}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, oauth_active: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Email Verified</label>
                    <select
                      value={formData.oauth_email_verified}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, oauth_email_verified: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Profile</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  {[
                    ["First Name", "profile_first_name"],
                    ["Middle Name", "profile_middle_name"],
                    ["Last Name", "profile_last_name"],
                    ["Suffix", "profile_suffix"],
                    ["Date of Birth", "profile_date_of_birth", "date"],
                    ["Gender", "profile_gender"],
                    ["Mobile", "profile_mobile_number"],
                    ["Email", "profile_email"],
                    ["Guardian Name", "profile_guardian_name"],
                    ["Relationship to Principal", "profile_relationship_to_principal"],
                  ].map(([label, key, type]) => (
                    <div key={key}>
                      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{label}</label>
                      <input
                        type={type || "text"}
                        value={formData[key]}
                        onChange={(event) =>
                          setFormData((prev) => ({ ...prev, [key]: event.target.value }))
                        }
                        className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                      />
                    </div>
                  ))}

                  <div className="md:col-span-2 xl:col-span-3">
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Home Address</label>
                    <textarea
                      value={formData.profile_home_address}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, profile_home_address: event.target.value }))
                      }
                      rows={3}
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Account Status</label>
                    <select
                      value={formData.profile_account_status_id}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, profile_account_status_id: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    >
                      <option value="">Not set</option>
                      {accountStatusOptions.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">Bank</h4>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Bank Name</label>
                    <select
                      value={formData.bank_id}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, bank_id: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    >
                      <option value="">Not set</option>
                      {banks.map((bank) => (
                        <option key={bank.id} value={bank.id}>
                          {bank.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Bank Account Name</label>
                    <input
                      value={formData.bank_account_name}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, bank_account_name: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Bank Account Number</label>
                    <input
                      value={formData.bank_account_number}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, bank_account_number: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Primary</label>
                    <select
                      value={formData.bank_is_primary}
                      onChange={(event) =>
                        setFormData((prev) => ({ ...prev, bank_is_primary: event.target.value }))
                      }
                      className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                    >
                      <option value="1">Yes</option>
                      <option value="0">No</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsEditOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSaving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isPasswordOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-6 py-8">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  OAuth Users
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">Reset Password</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsPasswordOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            {passwordError && (
              <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                {passwordError}
              </div>
            )}

            <form onSubmit={handleSavePassword} className="mt-6 space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.new_password}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({ ...prev, new_password: event.target.value }))
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={passwordForm.confirm_new_password}
                  onChange={(event) =>
                    setPasswordForm((prev) => ({
                      ...prev,
                      confirm_new_password: event.target.value,
                    }))
                  }
                  className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm focus:border-[var(--brand-orange)] focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsPasswordOpen(false)}
                  className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isPasswordSaving}
                  className="rounded-2xl bg-[var(--brand-orange)] px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isPasswordSaving ? "Saving..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemOauthUsers;
